import { useState, useEffect, useCallback, useRef } from 'react'
import { refreshAccessToken } from '@/lib/api/refresh'

export interface ToolCallEvent {
  type: 'tool_call'
  timestamp: string
  call_id?: string
  data: {
    tool_name: string
    status: 'started' | 'completed' | 'failed'
    params?: Record<string, unknown>
    result_preview?: unknown
    error?: string
    response_time_ms?: number
    project_id?: string | null
  }
}

export interface ConnectionEvent {
  type: 'connected'
  message: string
  user_uuid: string
}

export interface ErrorEvent {
  type: 'error'
  message: string
}

export interface SpectatorCountEvent {
  type: 'spectator_count'
  count: number
  timestamp: string
}

export interface ReactionEvent {
  type: 'reaction'
  emoji: string
  timestamp: string
}

export type WorkspaceEvent =
  | ToolCallEvent
  | ConnectionEvent
  | ErrorEvent
  | SpectatorCountEvent
  | ReactionEvent

interface UseWorkspaceSocketReturn {
  events: ToolCallEvent[]
  connected: boolean
  connecting: boolean
  error: string | null
  /** Number of spectators currently watching */
  spectatorCount: number
  /** Reaction emojis from spectators */
  reactions: Array<{ emoji: string; timestamp: string }>
  connect: () => void
  disconnect: () => void
  clearEvents: () => void
}

const MAX_EVENTS = 100
const STORAGE_KEY = 'workspace_events'

// Load persisted events from sessionStorage
function loadPersistedEvents(): ToolCallEvent[] {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as ToolCallEvent[]
      // Filter out stale 'started' events (older than 5 minutes)
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000
      return parsed.filter((e) => {
        if (e.data.status === 'started') {
          return new Date(e.timestamp).getTime() > fiveMinutesAgo
        }
        return true
      })
    }
  } catch {
    // Ignore parse errors
  }
  return []
}

// Save events to sessionStorage
function persistEvents(events: ToolCallEvent[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(events))
  } catch {
    // Ignore storage errors
  }
}

export function useWorkspaceSocket(): UseWorkspaceSocketReturn {
  const [events, setEvents] = useState<ToolCallEvent[]>(loadPersistedEvents)
  const [connected, setConnected] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [spectatorCount, setSpectatorCount] = useState(0)
  const [reactions, setReactions] = useState<Array<{ emoji: string; timestamp: string }>>([])

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const connectedAtRef = useRef<number | null>(null)
  const lastPongRef = useRef<number | null>(null)
  const missedPongsRef = useRef<number>(0)
  const connectingRef = useRef(false)

  // Persist events whenever they change
  useEffect(() => {
    persistEvents(events)
  }, [events])

  const clearEvents = useCallback(() => {
    setEvents([])
    sessionStorage.removeItem(STORAGE_KEY)
  }, [])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setConnected(false)
    setConnecting(false)
    connectingRef.current = false
  }, [])

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || connectingRef.current) {
      return
    }

    const token = localStorage.getItem('auth_token')
    if (!token) {
      setError('Not authenticated')
      return
    }

    setConnecting(true)
    connectingRef.current = true
    setError(null)

    // Presenter's own WS — always same-origin. Vite proxy (ws: true) upgrades
    // to the local central-api in dev; in prod nginx proxies through directly.
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${wsProtocol}//${window.location.host}/api/v1/workspace/ws?token=${token}`

    console.log('[useWorkspaceSocket] Connecting to:', wsUrl)

    try {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        connectedAtRef.current = Date.now()
        missedPongsRef.current = 0
        setConnected(true)
        setConnecting(false)
        connectingRef.current = false
        setError(null)
        console.log('[WS] connected')

        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            const sincePong = lastPongRef.current
              ? Math.round((Date.now() - lastPongRef.current) / 1000)
              : 'never'
            console.log(`[WS] ping (last pong ${sincePong}s ago, missed=${missedPongsRef.current})`)
            missedPongsRef.current++
            ws.send('ping')
          }
        }, 30000)
      }

      ws.onmessage = (event) => {
        if (event.data === 'pong') {
          lastPongRef.current = Date.now()
          missedPongsRef.current = 0
          return
        }

        try {
          const data = JSON.parse(event.data) as WorkspaceEvent

          if (data.type === 'tool_call') {
            const toolEvent = data as ToolCallEvent
            setEvents((prev) => {
              if (toolEvent.call_id && toolEvent.data.status !== 'started') {
                const existingIndex = prev.findIndex(
                  (e) => e.call_id === toolEvent.call_id && e.data.status === 'started',
                )
                if (existingIndex !== -1) {
                  // Remove the started event and prepend the completed one
                  // so events[0] always reflects the latest completion
                  const without = prev.filter((_, i) => i !== existingIndex)
                  return [toolEvent, ...without].slice(0, MAX_EVENTS)
                }
              }
              const newEvents = [toolEvent, ...prev]
              return newEvents.slice(0, MAX_EVENTS)
            })
          } else if (data.type === 'spectator_count') {
            const countData = data as unknown as Record<string, unknown>
            if (typeof countData.count === 'number') {
              setSpectatorCount(countData.count)
            }
          } else if (data.type === 'reaction') {
            const rData = data as unknown as Record<string, unknown>
            if (rData.emoji) {
              setReactions((prev) =>
                [
                  {
                    emoji: rData.emoji as string,
                    timestamp: (rData.timestamp as string) || new Date().toISOString(),
                  },
                  ...prev,
                ].slice(0, 50),
              )
            }
          } else if (data.type === 'error') {
            setError(data.message)
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e)
        }
      }

      ws.onclose = (event) => {
        const uptime = connectedAtRef.current
          ? Math.round((Date.now() - connectedAtRef.current) / 1000)
          : '?'
        const sincePong = lastPongRef.current
          ? Math.round((Date.now() - lastPongRef.current) / 1000)
          : 'never'
        console.warn(
          `[WS] closed code=${event.code} reason="${event.reason}" clean=${event.wasClean} uptime=${uptime}s lastPong=${sincePong}s ago`,
        )
        connectedAtRef.current = null
        setConnected(false)
        setConnecting(false)
        connectingRef.current = false

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current)
          pingIntervalRef.current = null
        }

        if (event.code === 1000) {
          return
        }

        if (event.code === 4001) {
          console.log('[WS] 4001 — attempting token refresh')
          refreshAccessToken().then((newToken) => {
            if (newToken) {
              console.log('[WS] token refreshed, reconnecting')
              connect()
            } else {
              console.error('[WS] token refresh failed — session dead')
              setError('Session expired')
              window.dispatchEvent(
                new CustomEvent('auth:unauthorized', { detail: { status: 401 } }),
              )
            }
          })
          return
        }

        console.log('[WS] unexpected close — reconnecting in 5s')
        reconnectTimeoutRef.current = setTimeout(() => {
          connect()
        }, 5000)
      }

      ws.onerror = () => {
        setError('WebSocket connection error')
        setConnecting(false)
        connectingRef.current = false
      }
    } catch {
      setError('Failed to create WebSocket connection')
      setConnecting(false)
      connectingRef.current = false
    }
  }, [])

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  return {
    events,
    connected,
    connecting,
    error,
    spectatorCount,
    reactions,
    connect,
    disconnect,
    clearEvents,
  }
}
